import React, { useState, useEffect, useRef } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { LoginPage } from './auth/login/LoginPage';
import NotFoundPage from './website/notfound/NotFound';
import Home from './website/home/Home';
import AboutAndContact from './website/about/About';
import { Registration } from './website/register/Register';
import { RegisterSuccess } from './website/register-success/RegisterSuccess';
import { jwtDecode } from 'jwt-decode';
import { ForgotPassword } from './website/forgot-password/ForgotPassword';
import { ResetPassword } from './website/reset-password/ResetPassword';
import PrivacyPolicy from './website/privacy-policy/PrivacyPolicy';
import PrivacyRequestPage from './website/privacy-request/PrivacyRequest';
import PricingPage from './website/pricing/Pricing';
import RayaAIPage from './website/ai/RayaAI';
import PointOfSalePage from './website/point-of-sale/PointOfSale';
import OnlineStorePage from './website/online-store/OnlineStore';
import ProjectManagementPage from './website/project-management/ProjectManagement';
import FleetManagementPage from './website/fleet-management/FleetManagement';

const decodeJWT = (token) => {
    try {
        return jwtDecode(token);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

// Without this, following a link from the bottom of one page leaves you at the
// bottom of the next. When the URL carries a hash (the nav links to sections
// such as /#industries or /pricing#estimate) the target is scrolled into view
// instead, retrying briefly because it may not have rendered yet. The home
// page's own anchor scrolling passes `state.scrollTo` and is left alone.
const ScrollToTop = () => {
    const { pathname, hash, state } = useLocation();
    const lastPath = useRef(pathname);

    useEffect(() => {
        const samePage = lastPath.current === pathname;
        lastPath.current = pathname;
        if (state?.scrollTo) return undefined;
        if (!hash) {
            window.scrollTo(0, 0);
            return undefined;
        }
        const id = decodeURIComponent(hash.slice(1));
        const timers = [0, 120, 400, 900].map((ms) => setTimeout(() => {
            const el = document.getElementById(id);
            if (el && !el.dataset.scrolled) {
                el.dataset.scrolled = '1';
                // A deep link can name a small control (a module tab) but ask for
                // its whole section to be shown via data-scroll-target.
                const target = (el.dataset.scrollTarget && document.getElementById(el.dataset.scrollTarget)) || el;
                target.scrollIntoView({ behavior: samePage ? 'smooth' : 'auto', block: 'start' });
                setTimeout(() => { delete el.dataset.scrolled; }, 1000);
            }
        }, ms));
        return () => timers.forEach(clearTimeout);
    }, [pathname, hash, state]);

    return null;
};

export const RayaRoutes = ({ isAuthenticated }) => {
    const [authenticatedSession, setAuthenticatedSession] = useState(false);

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (token) {
            const decodedToken = decodeJWT(token);
            const currentTime = new Date().getTime() / 1000;
            if (decodedToken && decodedToken.exp > currentTime) {
                setAuthenticatedSession(true);
            } else {
                setAuthenticatedSession(false);
            }
        } else {
            setAuthenticatedSession(false);
        }
        isAuthenticated(authenticatedSession);
    }, [authenticatedSession, isAuthenticated]);

    return (
        <>
        <ScrollToTop />
        <Routes>
            <Route path="/" element={<Home authSession={authenticatedSession} onLogin={(status) => isAuthenticated(status)} />} />
            <Route path="/login" element={<LoginPage authSession={authenticatedSession} onLogin={(status) => isAuthenticated(status)} />} />
            <Route path='/contact-us' element={<AboutAndContact />} />
            <Route path='/register' element={<Registration />} />
            <Route path='/register-success' element={<RegisterSuccess />} />
            <Route path='/forgot-password' element={<ForgotPassword />} />
            <Route path='/reset-password' element={<ResetPassword />} />
            <Route path='/pricing' element={<PricingPage />} />
            <Route path='/ai' element={<RayaAIPage />} />
            <Route path='/point-of-sale' element={<PointOfSalePage />} />
            <Route path='/online-store' element={<OnlineStorePage />} />
            <Route path='/project-management' element={<ProjectManagementPage />} />
            <Route path='/fleet-management' element={<FleetManagementPage />} />
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
            <Route path='/privacy-request' element={<PrivacyRequestPage />} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </>
    );
};
